import type { FeedEvent } from '@/shared/game/types'

/**
 * Грани кубиков и разбор броска из ленты.
 *
 * Сами кубики живут в центре доски (CentreDice): выпавшее видно там же, где
 * его бросали. Отдельного блока в панели больше нет — один и тот же бросок
 * в двух местах заставлял следить за двумя, а код — держать их в согласии.
 */

/** Расположение точек на гранях. Координаты в клетках сетки 3×3. */
const PIPS: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [
    [0, 0],
    [2, 2],
  ],
  3: [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  4: [
    [0, 0],
    [0, 2],
    [2, 0],
    [2, 2],
  ],
  5: [
    [0, 0],
    [0, 2],
    [1, 1],
    [2, 0],
    [2, 2],
  ],
  6: [
    [0, 0],
    [0, 2],
    [1, 0],
    [1, 2],
    [2, 0],
    [2, 2],
  ],
}

/**
 * Грань кубика.
 *
 * @param  spread  Насколько точки разнесены от центра грани. Единица — по
 *   углам сетки 3×3, как на настоящем кубике; меньше — точки сходятся
 *   к середине. Нужно там, где грань идёт значком мелкого размера: в углах
 *   крупной сетки точки читаются как четыре отдельные, а не как одна фигура.
 */
export function Face({ value, spread = 1 }: { value: number; spread?: number }) {
  /* Сжатие считается от середины грани (1.5), поэтому центральная точка
     остаётся на месте при любом spread. */
  const at = (index: number): number => 1.5 + (index + 0.5 - 1.5) * spread

  return (
    <svg viewBox="0 0 3 3" width="70%" height="70%" aria-hidden="true">
      {(PIPS[value] ?? []).map(([row, col]) => (
        <circle key={`${row}-${col}`} className="pip" cx={at(col)} cy={at(row)} r={0.32} />
      ))}
    </svg>
  )
}

/**
 * Последний бросок из ленты: чей, что выпало и номер события.
 *
 * Номер нужен, чтобы отличить новый бросок от того же самого: значения
 * повторяются постоянно, и по ним новизну не определить. Цвет — чтобы
 * понять, свой это бросок или соперника: показываются они по-разному.
 */
export function lastRoll(
  feed: FeedEvent[],
): { seq: number; dice: number[]; color: string | null } | null {
  const event = [...feed].reverse().find((candidate) => candidate.type === 'roll')

  if (event === undefined) return null

  return {
    seq: event.seq,
    dice: (event.payload.dice as number[]) ?? [],
    color: (event.payload.color as string) ?? null,
  }
}

/**
 * Какие из выпавших кубиков уже сыграны.
 *
 * Считается вычитанием мультимножества: две одинаковые шестёрки — это два
 * разных кубика, и сыгранная гасит только одну из них.
 */
export function usedFlags(rolled: number[], left: number[]): boolean[] {
  const rest = [...left]

  return rolled.map((value) => {
    const at = rest.indexOf(value)

    if (at === -1) return true

    rest.splice(at, 1)

    return false
  })
}
